import React, { useEffect, useState } from 'react';
import { Col, Container, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import Form from '../../components/Fields/Form';
import MultiSelectField from '../../components/Fields/MultiSelectField';
import NumberField from '../../components/Fields/NumberField';
import SelectListField from '../../components/Fields/SelectListField';
import SwitchField from '../../components/Fields/SwitchField';
import TextareaField from '../../components/Fields/TextareaField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import UserDefinedFieldModel from '../../models/UserDefinedFieldModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { Result } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import UserDefinedField_tab_ProductCategories from './Tabs/UserDefinedField_tab_ProductCategories';
import UserDefinedField_tab_SelectListItems from './Tabs/UserDefinedField_tab_SelectListItems';

const UserDefinedFieldView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const navigate = useNavigate()
    const [formData, setFormData] = useState<UserDefinedFieldModel>();

    const DataTypes = useSelectListOptions('api/v1/selectlist/DataTypes', '-- Select type');
    const ItemTypes = useSelectListOptions('api/v1/selectlist/ItemTypes?activeOnly=true');
    const ProjectTypes = useSelectListOptions('api/v1/selectlist/ProjectTypes?activeOnly=true');
    const BusinessPartnerTypes = useSelectListOptions('api/v1/selectlist/BusinessPartnerTypes');
    const UserDefinedFieldTypes = useSelectListOptions('api/v1/selectlist/UserDefinedFieldTypes', '-- Select type');
    const UserDefinedFieldCategories = useSelectListOptions('api/v1/selectlist/UserDefinedFieldCategories', '-- Select category');

    let idParams: IdParams = useParams();
    let userDefinedFieldId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        userDefinedFieldId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (userDefinedFieldId === undefined) {
            setFormData({
                id: 0,
                name: '',
                dataTypeId: 0,
                userDefinedFieldTypeId: 0,
                defaultValue: '',
                description: '',
                required: false,
                searchable: false,
                visibleEdit: true,
                visibleNew: true,
                itemTypeIds: [],
                projectTypeIds: [],
                businessPartnerTypeIds: [],
                concurrencyId: ''
            })
        }
        else {
            DataService.getById<UserDefinedFieldModel>('api/v1/userdefinedfields/', userDefinedFieldId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [userDefinedFieldId]);

    function submit() {
        if (formData) {
            if (formData.id === 0)
                DataService
                    .create<number>(`api/v1/userdefinedfields/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            navigate('/plm/itemsetup/userdefinedfield/' + response.data);
                    })
                    .catch(e => handleException(e));
            else
                DataService
                    .update<UserDefinedFieldModel>(`api/v1/userdefinedfields/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'userDefinedFieldTypeId', label: 'Type', validationRules: [{ type: 'isRequired' }] },
            { name: 'itemTypeIds', label: 'Item types' },
            { name: 'projectTypeIds', label: 'Project types' },
            { name: 'businessPartnerTypeIds', label: 'Business-partner types' },
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'dataTypeId', label: 'Data type', validationRules: [{ type: 'isRequired' }] },
            { name: 'userDefinedFieldCategoryId', label: 'Category'},
            { name: 'defaultValue', label: 'Default value' },
            { name: 'displayOrder', label: 'Display order' },
            { name: 'description', label: 'Description' },
            { name: 'required', label: 'Required', labelSize:8 },
            { name: 'searchable', label: 'Searchable', labelSize: 8 },
            { name: 'visibleNew', label: 'Visible for a new item', labelSize: 8 },
            { name: 'visibleEdit', label: 'Visible when editing an item', labelSize: 8 },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (userDefinedFieldId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.EditUdf)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    if (!formData)
        return <SpinnerOverlay />

    const showItemTypes = formData.userDefinedFieldTypeId == 1 || formData.userDefinedFieldTypeId == 3 || formData.userDefinedFieldTypeId == 6;
    const showProjectTypes = formData.userDefinedFieldTypeId == 4 || formData.userDefinedFieldTypeId == 6;
    const showBusinessPartnerTypes = formData.userDefinedFieldTypeId == 2;

    return (
        <div className='main-content'>
            <Form title='User defined field' footer={(
                <Row>
                    <Col md='8'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.EditUdf}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                    <Col md='4'>
                        <ButtonBar offset={0}>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='8'>
                            <SelectListField {...getFieldProps('userDefinedFieldTypeId')} options={UserDefinedFieldTypes} disabled={formData.id > 0} />
                            <MultiSelectField {...getFieldProps('itemTypeIds')} options={ItemTypes} visible={showItemTypes} />
                            <MultiSelectField {...getFieldProps('projectTypeIds')} options={ProjectTypes} visible={showProjectTypes} />
                            <MultiSelectField {...getFieldProps('businessPartnerTypeIds')} options={BusinessPartnerTypes} visible={showBusinessPartnerTypes} />
                            <TextboxField {...getFieldProps('name')} />
                            <SelectListField {...getFieldProps('dataTypeId')} options={DataTypes} />
                            <SelectListField {...getFieldProps('userDefinedFieldCategoryId')} options={UserDefinedFieldCategories} />
                            <TextboxField {...getFieldProps('defaultValue')} />
                            <NumberField {...getFieldProps('displayOrder')} />
                            <TextareaField {...getFieldProps('description')} />
                        </Col>
                        <Col md='4'>
                            <SwitchField {...getFieldProps('required')} />
                            <SwitchField {...getFieldProps('searchable')} />
                            <SwitchField {...getFieldProps('visibleNew')} />
                            <SwitchField {...getFieldProps('visibleEdit')} />
                        </Col>
                    </Row>
                </Container>
            </Form>
            {userDefinedFieldId && (formData.dataTypeId == 6 || formData.userDefinedFieldTypeId == 1) && (
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <HorizontalTabs
                                id='vtabs'
                                defaultActiveKey={formData.dataTypeId == 6 ? 'selectlistitems' : 'productCategories' }
                                tabs={[
                                    {
                                        title: 'Selectlist items',
                                        eventKey: 'selectlistitems',
                                        visible: formData.dataTypeId == 6,
                                        component: <UserDefinedField_tab_SelectListItems userDefinedField={formData} />,
                                    },
                                    {
                                        title: 'Product categories',
                                        eventKey: 'productCategories',
                                        visible: formData.userDefinedFieldTypeId == 1,
                                        component: <UserDefinedField_tab_ProductCategories id={userDefinedFieldId} />,
                                    },
                                ]}
                            />
                        </Col>
                    </Row>
                </Container>
            )}
        </div>
    );
}
export default UserDefinedFieldView;